import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Fetches and renders a Haiku-generated synthesis of prior messages in the
 * thread of `messageId`. Hidden entirely when there are no prior messages.
 */
export function ThreadSummaryCard({ messageId }: { messageId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: qk.threadSummary(messageId),
    queryFn: () => api.getThreadSummary(messageId),
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return <Skeleton className="h-16 w-full" />;
  }
  if (isError || !data || !data.summary || data.messageCount === 0) {
    return null;
  }

  return (
    <div className="rounded-md border border-border bg-muted/40 p-3">
      <div className="mb-1 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        <Sparkles className="h-3 w-3" /> Thread summary · {data.messageCount} prior{' '}
        {data.messageCount === 1 ? 'message' : 'messages'}
      </div>
      <div className="whitespace-pre-wrap break-words text-[13px] leading-[1.5] text-foreground/90">
        {data.summary}
      </div>
    </div>
  );
}
